import { useState, useEffect, useRef } from "react";

interface OptimizedHeroImageProps {
  src: string;
  webpSrc?: string;
  alt: string;
  className?: string;
  overlayClassName?: string;
  priority?: boolean;
}

const OptimizedHeroImage = ({
  src,
  webpSrc,
  alt,
  className = "",
  overlayClassName = "",
  priority = false,
}: OptimizedHeroImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (priority) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "100px",
        threshold: 0,
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  return (
    <div ref={imageRef} className={`absolute inset-0 ${className}`}>
      {/* Low quality placeholder */}
      <div
        className={`absolute inset-0 bg-muted transition-opacity duration-500 ${
          isLoaded ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Actual image with lazy loading */}
      {isInView && (
        <picture>
          {/* WebP format for modern browsers */}
          {webpSrc && (
            <source
              srcSet={webpSrc}
              type="image/webp"
            />
          )}
          {/* Fallback for all browsers */}
          <img
            src={src}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={priority ? "high" : "auto"}
            onLoad={() => setIsLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
              isLoaded ? "opacity-100" : "opacity-0"
            }`}
          />
        </picture>
      )}

      {/* Optional overlay */}
      {overlayClassName && (
        <div className={`absolute inset-0 ${overlayClassName}`} />
      )}
    </div>
  );
};

export default OptimizedHeroImage;
